// ScrollIndicatorEcho — Scroll-Indikator, Design "Echo (final)" von Claude
// Design (Handoff 2026-05-27).
//
// 6px breite, transparent-pulsende Pille (Laenge nach Storylaenge, s. lib/scroll-pille), links 10px innerhalb der Stage. Top
// linear gemappt auf scroll-percent. Idle-Pulse nach 3s ohne Scroll-Event.
// Cover (scroll≈0): opacity 0. Colophon (~100%): 2s warten → fade-out, no
// re-show.
//
// Tweak vs. Echo-Spec (Bene 27.05.): width 4→6px, opacity active 0.20→0.35,
// idle-pulse 0.18-0.34 → 0.30-0.55, plus dezenter Dark-Halo (box-shadow)
// für Kontrast auf hellen Renderer-Inhalten. Spec war unsichtbar auf
// bunt-bildreicher Bühne — Aufdrehen behält "leise", kommt aber durch.
//
// Token-Quelle: handoff/design-reference/echo-final.html (Spec-Tabelle dort).
// Konsumiert via ViewerChrome → ScrollyContainer als chromeOverlay-Sibling.
//
// Position-Formel (A): top = PAD_TOP + (viewportH − PAD_TOP − PAD_BOT − H) × p
// Idle-Trigger (B):    inactivity ≥ 3000 ms, OFF bei scroll/touch/wheel/pointer
// Cover (C):           opacity 0 wenn p < 0.001
// Colophon (D):        p ≥ 0.985 → 2s Timer → opacity 0, kein Re-Show
//                      "Kein Re-Show" interpretieren als "solange am Colophon".
//                      Scrollt User zurück in die Story (p < threshold), darf
//                      Echo wieder erscheinen. Sonst würde ein einziger End-
//                      Besuch (z.B. via Resume-Restore auf scrollPercent=1)
//                      Echo permanent killen — UX-buggy.

import { useEffect, useRef, useState } from 'react';
import { pillenLaenge } from '../../lib/scroll-pille';

interface Props {
  /** Scroll-Container dessen scrollTop/scrollHeight gelesen werden. */
  scrollContainerRef: React.RefObject<HTMLElement | null>;
  scrollSource?: 'container' | 'window';
}

const PAD_TOP = 60;
const PAD_BOTTOM = 24;
// Startwert bis zur ersten Messung. Danach richtet sich die Laenge nach der
// Story (lib/scroll-pille: 16 bis 120 px, Benedikt 29.09.2026) — vorher fest
// 24 px aus dem Echo-Handoff.
const ECHO_HEIGHT = 24;
const IDLE_DELAY_MS = 3000;
const COVER_THRESHOLD = 0.001;
const COLOPHON_THRESHOLD = 0.985;
const COLOPHON_FADE_DELAY_MS = 2000;

type Phase = 'cover' | 'active' | 'idle' | 'gone';

export function ScrollIndicatorEcho({
  scrollContainerRef,
  scrollSource = 'container',
}: Props) {
  const echoRef = useRef<HTMLDivElement | null>(null);
  const pilleRef = useRef<HTMLDivElement | null>(null);
  const phaseRef = useRef<Phase>('cover');
  const lastActivityRef = useRef<number>(Date.now());
  const [phase, setPhaseState] = useState<Phase>('cover');
  // ANFASSBAR (Jans Wunsch 25.08.2026, wie der Balken der Admin-Vorschau):
  // Daumen greifen und ziehen treibt den Scroll — Uebersetzung Balken-px →
  // Scroll-px wird pro Frame aktuell gehalten (URL-Bar/Resize-fest).
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{ startY: number; startScrollTop: number } | null>(null);
  const ratioRef = useRef(0);
  const maxScrollRef = useRef(0);

  // Mirror state → ref so rAF can read latest without re-subscribing.
  phaseRef.current = phase;

  useEffect(() => {
    const echo = echoRef.current;
    if (!echo) return;

    let raf = 0;
    let colophonTimer = 0;

    const setPhase = (next: Phase) => {
      if (phaseRef.current === next) return;
      phaseRef.current = next;
      setPhaseState(next);
    };

    let letzterScrollTop = -1;
    let letztePillenH = ECHO_HEIGHT;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      // Container LIVE lesen — nicht die Mount-Zeit-Referenz: ViewerChrome
      // remountet den Scroll-Container beim START (Stage-Gating), eine
      // eingefangene Referenz zeigt danach auf den ABGEHAENGTEN Knoten und
      // liest ewig scrollTop 0 (gleiches Stale-Node-Muster wie beim
      // Nav-Knopf-Fix #450; Fund beim Anfassbar-Umbau 25.08.2026).
      const elJetzt =
        scrollSource === 'window' ? null : scrollContainerRef.current;
      const viewportH =
        scrollSource === 'window'
          ? window.innerHeight || document.documentElement.clientHeight
          : (elJetzt?.clientHeight ?? 0);
      const scrollTop =
        scrollSource === 'window'
          ? window.scrollY ||
            document.documentElement.scrollTop ||
            document.body.scrollTop ||
            0
          : (elJetzt?.scrollTop ?? 0);
      const scrollHeight =
        scrollSource === 'window'
          ? Math.max(
              document.documentElement.scrollHeight,
              document.body.scrollHeight,
            )
          : (elJetzt?.scrollHeight ?? 0);
      // Scroll-Bewegung = Aktivitaet (ersetzt die scroll-Listener am ggf.
      // ausgetauschten Container; wheel/touch/pointer laufen ueber window).
      if (letzterScrollTop !== -1 && scrollTop !== letzterScrollTop) {
        lastActivityRef.current = Date.now();
      }
      letzterScrollTop = scrollTop;
      const max = scrollHeight - viewportH;
      const percent = max > 0 ? Math.max(0, Math.min(1, scrollTop / max)) : 0;

      // Viewport-Höhe live aus dem aktiven Scroll-Root — ändert sich z.B.
      // wenn iOS-Safari die URL-Bar einklappt.
      // PILLENLAENGE nach Storylaenge (sichtbarer Anteil an der ganzen
      // Story). Nur bei Aenderung ins DOM schreiben.
      const spur = Math.max(0, viewportH - PAD_TOP - PAD_BOTTOM);
      const pillenH = pillenLaenge(spur, viewportH, scrollHeight) || ECHO_HEIGHT;
      if (pillenH !== letztePillenH) {
        letztePillenH = pillenH;
        echo.style.height = `${pillenH}px`;
        if (pilleRef.current) pilleRef.current.style.height = `${pillenH}px`;
      }
      // Weg der Pille = Spur minus eigene Laenge; das Ziehen rechnet ueber
      // ratioRef mit demselben Weg, damit die Pille unter dem Finger bleibt.
      const trackH = Math.max(0, spur - pillenH);

      ratioRef.current = trackH > 0 ? max / trackH : 0;
      maxScrollRef.current = Math.max(0, max);

      // Position update — direkt DOM, kein State (avoid 60Hz React-Renders).
      echo.style.top = `${PAD_TOP + trackH * percent}px`;

      // Phase-Übergänge
      if (percent < COVER_THRESHOLD) {
        if (colophonTimer) {
          window.clearTimeout(colophonTimer);
          colophonTimer = 0;
        }
        setPhase('cover');
      } else if (percent >= COLOPHON_THRESHOLD) {
        // D: erreicht 100% → 2s warten → fade-out (nur wenn nicht bereits gone)
        if (phaseRef.current !== 'gone' && !colophonTimer) {
          colophonTimer = window.setTimeout(() => {
            setPhase('gone');
            colophonTimer = 0;
          }, COLOPHON_FADE_DELAY_MS);
        }
      } else {
        // Zurück in die Story — Colophon-Timer abbrechen, "gone" verlassen.
        if (colophonTimer) {
          window.clearTimeout(colophonTimer);
          colophonTimer = 0;
        }
        // B: idle wenn ≥ IDLE_DELAY_MS keine Activity
        const elapsed = Date.now() - lastActivityRef.current;
        setPhase(elapsed >= IDLE_DELAY_MS ? 'idle' : 'active');
      }
    };

    // B: jede Activity setzt den Idle-Timer-Anker zurück
    const onActivity = () => {
      lastActivityRef.current = Date.now();
    };
    // Aktivitaets-Listener auf window (bubbelnde Gesten); Scroll-Aktivitaet
    // erkennt der Tick selbst an der Bewegung. So gibt es nichts, was an
    // einem remounteten Container neu verkabelt werden muesste.
    window.addEventListener('touchstart', onActivity, { passive: true });
    window.addEventListener('wheel', onActivity, { passive: true });
    window.addEventListener('pointerdown', onActivity, { passive: true });
    lastActivityRef.current = Date.now();
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      if (colophonTimer) window.clearTimeout(colophonTimer);
      window.removeEventListener('touchstart', onActivity);
      window.removeEventListener('wheel', onActivity);
      window.removeEventListener('pointerdown', onActivity);
    };
  }, [scrollContainerRef, scrollSource]);

  const opacity = phase === 'cover' || phase === 'gone' ? 0 : 1;
  const isIdle = phase === 'idle';

  const leseScrollTop = () =>
    scrollSource === 'window'
      ? window.scrollY || document.documentElement.scrollTop || 0
      : (scrollContainerRef.current?.scrollTop ?? 0);
  const schreibeScrollTop = (wert: number) => {
    const ziel = Math.max(0, Math.min(maxScrollRef.current, wert));
    if (scrollSource === 'window') {
      window.scrollTo(0, ziel);
    } else if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = ziel;
    }
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (opacity === 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { startY: e.clientY, startScrollTop: leseScrollTop() };
    lastActivityRef.current = Date.now();
    setDragging(true);
    // Gurt gegen verlorene pointerup-Events (Safari-Capture-Falle, s.o.):
    // Fenster-weit einmalig loesen — greift auch, wenn das Element-Event
    // nie ankommt (z.B. Zeiger verlaesst das Fenster, App-Wechsel).
    const loese = () => {
      dragRef.current = null;
      setDragging(false);
      window.removeEventListener('pointerup', loese, true);
      window.removeEventListener('pointercancel', loese, true);
      window.removeEventListener('blur', loese);
    };
    window.addEventListener('pointerup', loese, true);
    window.addEventListener('pointercancel', loese, true);
    window.addEventListener('blur', loese);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const anker = dragRef.current;
    if (!anker) return;
    // Safari verliert nach setPointerCapture gern das pointerup (die Pille
    // bewegt sich beim Schreiben von scrollTop unterm Zeiger weg) — der
    // Drag blieb als Geist haengen und JEDE Mausbewegung schrieb scrollTop
    // zurueck (Jans Hakel-Fund am Scrolly-Ende, ?scrolldebug-Overlay
    // 27.08.2026). Fuer MAUS-Pointer ist buttons verlaesslich: 0 heisst
    // keine Taste unten → Geist-Drag loesen. (Touch/Pen ausgenommen —
    // dort meldet Safari buttons unzuverlaessig.)
    if (e.pointerType === 'mouse' && e.buttons === 0) {
      onPointerUp(e);
      return;
    }
    lastActivityRef.current = Date.now();
    const dy = e.clientY - anker.startY;
    schreibeScrollTop(anker.startScrollTop + dy * ratioRef.current);
  };
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    dragRef.current = null;
    setDragging(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* Capture kann schon weg sein */
    }
  };

  return (
    <>
      <style>{`
        @keyframes scrollyEchoIdle {
          0%, 100% { background: rgba(255, 255, 255, 0.30); }
          50%      { background: rgba(255, 255, 255, 0.55); }
        }
      `}</style>
      {/* Aeussere GREIFZONE: bekommt das top-Tracking (echoRef) und die
          Pointer-Handler. Breiter als die 6-px-Pille, damit man sie ohne
          Zielwasser trifft; bewegt sich mit der Pille mit. Waehrend des
          Ziehens keine top-Transition (sonst laeuft der Daumen dem Zeiger
          hinterher). pointerEvents:auto uebersteuert den pointerEvents:none-
          Wrapper des Body-Scroll-Modus (ViewerChrome chromeOverlays). */}
      <div
        ref={echoRef}
        aria-hidden="true"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{
          position: 'absolute',
          left: 0,
          top: PAD_TOP,
          width: 28,
          height: ECHO_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          transition: dragging
            ? 'opacity 400ms ease'
            : 'top 50ms linear, opacity 400ms ease',
          pointerEvents: opacity === 0 ? 'none' : 'auto',
          cursor: dragging ? 'grabbing' : 'grab',
          touchAction: 'none',
          // Hoher z-index notwendig: scrolly-scroll-container Kinder haben
          // z-Werte bis 1000 (slide-container) und 998 (sticky-Element).
          // Trotz isolation:isolate auf scroll-container greift's hier nicht
          // zuverlässig — sicherheitshalber 9999.
          zIndex: 9999,
          opacity,
        }}
      >
        <div
          ref={pilleRef}
          style={{
            marginLeft: 10,
            width: 6,
            height: ECHO_HEIGHT,
            borderRadius: 999,
            background: dragging
              ? 'rgba(255, 255, 255, 0.65)'
              : 'rgba(255, 255, 255, 0.35)',
            // Dark-Halo: macht Echo auf hellen Renderer-Inhalten sichtbar,
            // verschwindet auf dunklen optisch. Sehr dezent, nicht aufdringlich.
            boxShadow: '0 0 4px rgba(0, 0, 0, 0.5)',
            animation: isIdle && !dragging
              ? 'scrollyEchoIdle 2.4s ease-in-out infinite'
              : 'none',
          }}
        />
      </div>
    </>
  );
}
